import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ayreneService, decodeJWT } from "../../services/ayrene.service";
import { userIdMappingService } from "../../services/userIdMapping.service";

const DeleteAccount: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();

    const handleDeleteAccount = async (): Promise<void> => {
        const token = localStorage.getItem("authToken");
        const userId = localStorage.getItem("userId");
        const ayreneAuthToken = localStorage.getItem("ayreneAuthToken");

        if (!token || !userId) {
            Swal.fire("Error", "User authentication details not found. Please login again.", "error");
            return;
        }

        if (!ayreneAuthToken) {
            Swal.fire("Error", "Cannot delete account. Ayrene authentication not found. Please login again.", "error");
            return;
        }

        const confirmDelete = await Swal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete my account!",
        });

        if (!confirmDelete.isConfirmed) return;

        setLoading(true);

        try {
            const decoded = decodeJWT(token);
            const userEmail = decoded?.email || decoded?.sub;

            if (!userEmail) {
                setLoading(false);
                Swal.fire("Error", "Cannot delete account. User information not found. Please login again.", "error");
                return;
            }

            console.log('🔄 Step 1: Deleting Ayrene profile...');
            const ayreneDeleteResult = await ayreneService.deleteProfileByEmail(userEmail);

            if (!ayreneDeleteResult.success) {
                setLoading(false);
                console.error('❌ Ayrene profile deletion failed:', ayreneDeleteResult.message);

                Swal.fire({
                    title: "Ayrene Deletion Failed",
                    text: ayreneDeleteResult.message || "Failed to delete Ayrene profile. Please try again or contact support.",
                    icon: "error",
                    confirmButtonText: "OK"
                });
                return;
            }

            console.log('✅ Ayrene profile deleted successfully!');

            console.log('🔄 Step 2: Deleting Fervoir account...');
            const response = await axios.delete(
                `https://nrimarriage.in/api/v1/users/DeleteUser/${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.status === 200) {
                console.log('✅ Fervoir account deleted successfully!');

                console.log('🔄 Step 3: Cleaning up cache and storage...');
                userIdMappingService.clearCacheForUser(userId);

                localStorage.removeItem("authToken");
                localStorage.removeItem("userId");
                localStorage.removeItem("ayreneAuthToken");
                localStorage.removeItem("ayreneRefreshToken");
                localStorage.removeItem("ayreneTokenExpiration");
                localStorage.removeItem("ayreneLoginStatus");

                console.log('✅ All data cleaned up successfully!');

                Swal.fire("Deleted!", "Your account has been deleted from both Fervoir and Ayrene.", "success");
                navigate("/login");
            }
        } catch (error: any) {
            console.error("❌ Error during account deletion:", error);

            let errorMessage = "Failed to delete account. Please try again later.";

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                if (error.message.includes("Network Error")) {
                    errorMessage = "Network error. Please check your connection and try again.";
                } else {
                    errorMessage = error.message;
                }
            }

            Swal.fire("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            className="dropdown-item"
            onClick={handleDeleteAccount}
            disabled={loading}
        >
            {loading ? "Deleting..." : "Delete Account"}
        </button>
    );
};

export default DeleteAccount;
